import React, { useState, useEffect } from 'react';
import { Modal, Box, Button } from '@mui/material';

import type { ExamAssignmentFullDTO } from '../models/assignmentDTOs';
import { apiRequest } from '@/src/services';
import { Loading, ErrorMessage, StatusMessage } from '@/src/ui';

interface DeleteModalProps {
    open: boolean;
    resourceId: number | null;
    handleClose: (_shouldRefresh: boolean, _isInTimeout: boolean) => void;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ open, resourceId, handleClose }) => {
    const [hasLoaded, setHasLoaded] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [firstConfirm, setFirstConfirm] = useState(false);

    const [data, setData] = useState<ExamAssignmentFullDTO | null>(null);

    useEffect(() => {
        (async function init() {
            let res = null;
            try {
                res = await apiRequest<ExamAssignmentFullDTO>(
                    'GET',
                    `assessments/examAssignments/${resourceId}`
                );
            } catch {
                setError('Failed to load assessment assignment data');
                return;
            } finally {
                setHasLoaded(true);
            }

            setData(res.data);
        })();
    }, [resourceId]);

    const handleSubmit = async (e: React.FormEvent<any>) => {
        e.preventDefault();

        if (submitting || success) return;

        if (!data?.id) setError('Unable to load assessment assignment ID');

        if (!firstConfirm) {
            setFirstConfirm(true);
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            await apiRequest<number>('DELETE', `assessments/examAssignments/${data?.id}`);
            setSuccess(true);
            setTimeout(() => {
                handleClose(true, false);
                setFirstConfirm(false);
                setSuccess(false);
            }, 750);
        } catch {
            setError('Failed to delete assessment');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={() => handleClose(false, success)}>
            <Box sx={{ p: 4, bgcolor: 'background.paper', margin: 'auto', mt: 8, width: 400 }}>
                <h2>Delete Assessment</h2>
                <br></br>
                {!hasLoaded ? (
                    <Loading message="Loading..." size="lg" />
                ) : data ? (
                    <>
                        <p>Are you sure you want to delete this assessment?</p>
                    </>
                ) : (
                    <p>No assessment selected.</p>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    {!firstConfirm ? (
                        <>
                            <Button variant="outlined" onClick={() => handleClose(false, false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="error" onClick={handleSubmit}>
                                Delete
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                disabled={submitting || success}
                                variant="contained"
                                color="error"
                                onClick={handleSubmit}
                            >
                                Confirm Delete
                            </Button>
                            <Button
                                disabled={submitting || success}
                                variant="outlined"
                                onClick={() => handleClose(false, false)}
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                </Box>
                <ErrorMessage error={error} />
                <StatusMessage
                    visible={success}
                    status="Assessment assignment deleted successfully!"
                    style={{ marginTop: 8 }}
                />
                <Loading visible={submitting} message="Processing..." size="sm" />
            </Box>
        </Modal>
    );
};

export default DeleteModal;
